/**
 * View Transitions — non-critical enhancements.
 *
 * The critical path (setting 'page-navigation' transition type and
 * removing the render blocker for reduced-motion) is handled by an
 * inline <script> in theme.liquid to guarantee synchronous execution
 * before pageswap/pagereveal fire.
 *
 * This file adds:
 * - INP optimization: skip transition on user interaction
 * - Debug mode: header persistence diagnostics (Theme Settings → Developer)
 *
 * @see https://developer.chrome.com/docs/web-platform/view-transitions
 */
(function () {
	/**
	 * INP optimization: cancel view transition if user interacts during it.
	 * Prevents the animation from delaying the next paint.
	 * Desktop only: on touch devices nearly every navigation is followed by a
	 * touch (scroll intent), which killed the transition on most back-navs —
	 * without it there is no old-page snapshot, so the user saw a white flash
	 * and the page assembling progressively (header "arriving late"). The
	 * ::view-transition overlay has pointer-events: none, so keeping the
	 * transition alive doesn't block interaction anyway.
	 */
	if (!window.matchMedia('(hover: none)').matches) {
		window.addEventListener('pageswap', (event) => {
			if (!('viewTransition' in event) || !(event.viewTransition instanceof ViewTransition)) return;

			for (const name of ['pointerdown', 'keydown']) {
				document.addEventListener(name, () => event.viewTransition.skipTransition(), { once: true });
			}
		});
	}

	// ═══════════════════════════════════════════════════════════════════════════
	// DEBUG: Header Persistence
	// Enabled via Theme Settings → Developer → Debug Header Persistence
	// ═══════════════════════════════════════════════════════════════════════════

	if (window.__THEME_DEBUG_HEADER__) {
		const PREFIX = '%c[Header Debug]%c';
		const STYLE = ['color:#888;font-weight:bold', 'color:inherit'];
		const header = document.querySelector('header');

		if (!header) {
			console.warn('[Header Debug]', 'No <header> element found');
		} else {
			const blocker = document.getElementById('view-transition-render-blocker');
			const hasSkip = header.hasAttribute('data-skip-node-update');

			const allGood = hasSkip && !!blocker;

			console.groupCollapsed(
				`${PREFIX} ${allGood ? '%c✅ All checks passed' : '%c⚠️ Issues found'} — expand for details`,
				...STYLE, allGood ? 'color:#0f0' : 'color:#ff0'
			);
			console.log(`${blocker ? '✅' : '⚠️'} Render blocker: ${blocker ? 'present' : 'absent (ok — removed for first/external loads, reloads, reduced motion)'}`);
			console.log(`${hasSkip ? '✅' : '❌'} data-skip-node-update on <header> (for morph() calls within same page)`);
			console.log('');
			console.log('ℹ️  Header group has its own view-transition-name (header).');
			console.log('   Its OLD snapshot stays visible for the whole transition — guaranteed pixels even if the new page is mid-paint.');
			console.log('ℹ️  Transition type is set by inline <script> in theme.liquid (synchronous).');
			console.groupEnd();

			window.addEventListener('pageswap', () => {
				console.log(`${PREFIX} %c⚡ pageswap fired%c — old page snapshot taken`, ...STYLE, 'color:#0f0', 'color:inherit');
			});

			window.addEventListener('pagereveal', () => {
				console.log(`${PREFIX} %c⚡ pagereveal fired%c — new page revealed with transition`, ...STYLE, 'color:#0f0', 'color:inherit');
			});

			const INIT_ATTRS = new Set(['data-initialized', 'aria-controls', 'aria-expanded', 'hidden', 'aria-hidden', 'role', 'tabindex']);
			let initMutations = 0;
			let otherMutations = 0;

			new MutationObserver((mutations) => {
				for (const m of mutations) {
					const isInit = m.type === 'attributes' && INIT_ATTRS.has(m.attributeName);
					if (isInit) {
						initMutations++;
					} else {
						otherMutations++;
						console.log(
							`${PREFIX} mutation: %c${m.type}%c on %c${m.target.nodeName}%c`,
							...STYLE, 'color:#ff0', 'color:inherit', 'color:#0ff', 'color:inherit',
							m.type === 'childList'
								? `+${m.addedNodes.length} -${m.removedNodes.length}`
								: `attr="${m.attributeName}"`
						);
					}
				}
			}).observe(header, { childList: true, subtree: true, attributes: true });

			setTimeout(() => {
				console.log(
					`${PREFIX} Init complete: %c${initMutations} component init mutations%c (expected), %c${otherMutations} other mutations%c`,
					...STYLE, 'color:#888', 'color:inherit', otherMutations === 0 ? 'color:#0f0' : 'color:#ff0', 'color:inherit'
				);
			}, 1000);
		}
	}
	// ═══════════════════════════════════════════════════════════════════════════
	// DEBUG: BFCache & Back-Navigation Diagnostics
	// Uses the same debug flag as header persistence
	// ═══════════════════════════════════════════════════════════════════════════

	if (window.__THEME_DEBUG_EVENTS__ || window.__THEME_DEBUG_HEADER__) {
		const BF = '%c[BackNav]%c';
		const BS = ['color:#888;font-weight:bold', 'color:inherit'];

		window.addEventListener('pageshow', (event) => {
			const nav = performance.getEntriesByType('navigation')[0];
			const navType = nav?.type || 'unknown';

			if (event.persisted) {
				console.log(
					`${BF} %c✅ BFCache hit%c — instant restore (${Math.round(performance.now())}ms)`,
					...BS, 'color:#0f0', 'color:inherit'
				);
				return;
			}

			if (navType !== 'back_forward') return;

			// Back/forward without BFCache — diagnose why
			const ttfb = nav ? Math.round(nav.responseStart) : '?';
			const interactive = nav ? Math.round(nav.domInteractive) : '?';
			const loaded = nav ? Math.round(nav.loadEventEnd || performance.now()) : '?';
			const wasPrerendered = nav?.activationStart > 0;

			if (wasPrerendered) {
				console.log(
					`${BF} %c✅ Prerender activated%c — ${Math.round(nav.activationStart)}ms activation`,
					...BS, 'color:#0f0', 'color:inherit'
				);
			} else {
				console.warn(
					`${BF} %c❌ BFCache miss%c — full reload (TTFB: ${ttfb}ms, interactive: ${interactive}ms, loaded: ${loaded}ms)`,
					...BS, 'color:#f80', 'color:inherit'
				);
			}

			// Chrome 123+: detailed reasons why BFCache failed
			if (nav?.notRestoredReasons) {
				const nr = nav.notRestoredReasons;
				console.groupCollapsed(`${BF} NotRestoredReasons`, ...BS);
				console.log('Blocked by:', nr.reasons?.map(r => r.reason || r.toString()).join(', ') || '(none)');
				for (const child of nr.children || []) {
					if (child.reasons?.length) {
						console.log(`↳ iframe [${child.id || child.src}]:`, child.reasons.join(', '));
					}
				}
				console.groupEnd();
			} else if (!wasPrerendered) {
				console.log(
					`${BF} ℹ️ Use chrome://flags/#back-forward-cache to enable BFCache debug, or check Application → Back/Forward Cache in DevTools`,
					...BS
				);
			}
		});
	}
})();
